import type { ReactNode } from "react";
import styles from "./EmptyState.module.css";

interface EmptyStateProps {
  /** Optional. When present, sized 32px, `--text-tertiary`. */
  icon?: ReactNode;
  title: string;
  body?: string;
  /** Usually a <Button>. */
  action?: ReactNode;
}

/**
 * EmptyState — an invitation, not an apology.
 *
 * The copy convention on this component is deliberate: never "No
 * results found", never "Sorry, nothing here". Say what CAN be done.
 * "No transactions yet — Add money to get started." Not "You have 0
 * transactions." The rule reads as pedantic on paper but matters in
 * live product: an empty inbox on the first day should feel like a
 * clean slate, not a failure.
 *
 * Filtered-to-zero is a SEPARATE state (rendered by Table's own
 * empty-slot logic when a search+filter combination clears the row
 * set). Do not conflate them.
 */
export function EmptyState({ icon, title, body, action }: EmptyStateProps) {
  return (
    <div className={styles.empty} role="status">
      {icon ? <span className={styles.icon}>{icon}</span> : null}
      <h3 className={styles.title}>{title}</h3>
      {body ? <p className={styles.body}>{body}</p> : null}
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
}
